import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { useTranslation } from "@/contexts/LanguageContext";

interface ConfigurationEffectProps {
  onComplete: () => void;
}

export function ConfigurationEffect({ onComplete }: ConfigurationEffectProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState(false);

  const steps = [
    t('onboarding.config.step1'),
    t('onboarding.config.step2'),
    t('onboarding.config.step3'),
    t('onboarding.config.step4'),
  ];

  useEffect(() => {
    const stepDuration = 750;
    const totalSteps = steps.length;

    const interval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev < totalSteps - 1) return prev + 1;
        return prev;
      });
    }, stepDuration);

    const completeTimeout = setTimeout(() => {
      setCompleted(true);
      setTimeout(onComplete, 800);
    }, stepDuration * totalSteps);

    // Safety timeout: force completion after 10 seconds max
    const safetyTimeout = setTimeout(() => {
      setCompleted(true);
      onComplete();
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(completeTimeout);
      clearTimeout(safetyTimeout);
    };
  }, [onComplete, steps.length]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-background"
    >
      {/* Shimmer wave background */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          initial={{ x: "-100%" }}
          animate={{ x: "200%" }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute inset-0 w-1/2"
          style={{
            background: "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.1), transparent)",
          }}
        />
        <motion.div
          initial={{ x: "-100%" }}
          animate={{ x: "200%" }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "linear",
            delay: 0.3,
          }}
          className="absolute inset-0 w-1/3"
          style={{
            background: "linear-gradient(90deg, transparent, hsl(var(--accent) / 0.08), transparent)",
          }}
        />
      </div>

      <div className="relative z-10 text-center space-y-8 px-4">
        {/* Animated icon */}
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: completed ? 1.1 : 1, rotate: completed ? 0 : 360 }}
          transition={{
            rotate: { duration: 2, repeat: completed ? 0 : Infinity, ease: "linear" },
            scale: { duration: 0.3 },
          }}
          className="mx-auto w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center"
        >
          {completed ? (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <CheckCircle2 className="w-12 h-12 text-success" />
            </motion.div>
          ) : (
            <Sparkles className="w-12 h-12 text-primary" />
          )}
        </motion.div>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl font-bold text-foreground"
        >
          {completed ? t('onboarding.config.ready') : t('onboarding.config.configuring')}
        </motion.h2>

        {/* Steps progress */}
        <div className="space-y-3 max-w-sm mx-auto">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{
                opacity: index <= currentStep ? 1 : 0.3,
                x: 0,
              }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center gap-3 text-left"
            >
              {index < currentStep ? (
                <CheckCircle2 className="w-5 h-5 text-success shrink-0" />
              ) : index === currentStep ? (
                <Loader2 className="w-5 h-5 text-primary animate-spin shrink-0" />
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-muted shrink-0" />
              )}
              <span
                className={
                  index <= currentStep ? "text-foreground" : "text-muted-foreground"
                }
              >
                {step}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Progress bar */}
        <motion.div className="w-64 h-1.5 bg-muted rounded-full overflow-hidden mx-auto">
          <motion.div
            initial={{ width: "0%" }}
            animate={{ width: completed ? "100%" : `${((currentStep + 1) / steps.length) * 100}%` }}
            transition={{ duration: 0.5 }}
            className="h-full bg-primary rounded-full"
          />
        </motion.div>
      </div>
    </motion.div>
  );
}
